import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import axiosInstance from '../../../api/axiosInstance';
import AlertModal from '../../../reusable/AlertModal'; 
import imageCompression from 'browser-image-compression';
import ShareModal from '../../../modals/ShareModal';

const placeholderImageBase64 = "data:image/gif;base64,R0lGODlhAQABAIAAAMLCwgAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==";

const locationData = [
  { state: "Karnataka", cities: ["Bangalore", "Mysore", "Hubli-Dharwad", "Mangalore", "Belgaum", "Gulbarga", "Davanagere", "Bellary", "Bijapur", "Shimoga", "Tumkur", "Raichur", "Bidar", "Hassan", "Udupi", "Chikmagalur", "Karwar", "Kolar", "Chitradurga", "Haveri", "Mandya", "Gadag", "Bagalkot", "Koppal", "Yadgir", "Chamrajnagar", "Ramanagara", "Chikkaballapur", "Madikeri"] },
  { state: "Andaman and Nicobar Islands", cities: ["Port Blair", "Garacharma", "Bambooflat"] },
  { state: "Andhra Pradesh", cities: ["Visakhapatnam", "Vijayawada", "Guntur", "Nellore", "Kurnool"] },
  { state: "Arunachal Pradesh", cities: ["Itanagar", "Tawang", "Naharlagun", "Pasighat"] },
  { state: "Assam", cities: ["Guwahati", "Silchar", "Dibrugarh", "Jorhat", "Nagaon"] },
  { state: "Bihar", cities: ["Patna", "Gaya", "Bhagalpur", "Muzaffarpur", "Purnia"] },
  { state: "Chandigarh", cities: ["Chandigarh"] },
  { state: "Chhattisgarh", cities: ["Raipur", "Bhilai", "Bilaspur", "Korba", "Durg"] },
  { state: "Dadra and Nagar Haveli and Daman and Diu", cities: ["Daman", "Diu", "Silvassa"] },
  { state: "Delhi", cities: ["New Delhi", "North Delhi", "South Delhi", "West Delhi", "East Delhi"] },
  { state: "Goa", cities: ["Panaji", "Margao", "Vasco da Gama", "Mapusa", "Ponda"] },
  { state: "Gujarat", cities: ["Ahmedabad", "Surat", "Vadodara", "Rajkot", "Bhavnagar"] },
  { state: "Haryana", cities: ["Gurugram", "Faridabad", "Panipat", "Ambala", "Rohtak"] },
  { state: "Himachal Pradesh", cities: ["Shimla", "Manali", "Dharamshala", "Mandi", "Solan"] },
  { state: "Jammu and Kashmir", cities: ["Srinagar", "Jammu", "Anantnag", "Baramulla", "Kathua"] },
  { state: "Jharkhand", cities: ["Ranchi", "Jamshedpur", "Dhanbad", "Bokaro", "Deoghar"] },
  { state: "Kerala", cities: ["Thiruvananthapuram", "Kochi", "Kozhikode", "Thrissur", "Kollam"] },
  { state: "Ladakh", cities: ["Leh", "Kargil"] },
  { state: "Lakshadweep", cities: ["Kavaratti", "Agatti", "Minicoy"] },
  { state: "Madhya Pradesh", cities: ["Indore", "Bhopal", "Jabalpur", "Gwalior", "Ujjain"] },
  { state: "Maharashtra", cities: ["Mumbai", "Pune", "Nagpur", "Nashik", "Kolhapur", "Aurangabad", "Solapur"] },
  { state: "Manipur", cities: ["Imphal", "Thoubal", "Bishnupur", "Churachandpur"] },
  { state: "Meghalaya", cities: ["Shillong", "Tura", "Nongstoin", "Jowai"] },
  { state: "Mizoram", cities: ["Aizawl", "Lunglei", "Saiha", "Champhai"] },
  { state: "Nagaland", cities: ["Kohima", "Dimapur", "Mokokchung", "Tuensang"] },
  { state: "Odisha", cities: ["Bhubaneswar", "Cuttack", "Rourkela", "Berhampur", "Puri"] },
  { state: "Puducherry", cities: ["Puducherry", "Ozhukarai", "Karaikal", "Yanam", "Mahe"] },
  { state: "Punjab", cities: ["Ludhiana", "Amritsar", "Jalandhar", "Patiala", "Bathinda"] },
  { state: "Rajasthan", cities: ["Jaipur", "Jodhpur", "Udaipur", "Kota", "Bikaner"] },
  { state: "Sikkim", cities: ["Gangtok", "Namchi", "Gyalshing", "Mangan"] },
  { state: "Tamil Nadu", cities: ["Chennai", "Coimbatore", "Madurai", "Tiruchirappalli", "Salem"] },
  { state: "Telangana", cities: ["Hyderabad", "Warangal", "Nizamabad", "Karimnagar", "Khammam"] },
  { state: "Tripura", cities: ["Agartala", "Dharmanagar", "Udaipur", "Kailashahar"] },
  { state: "Uttar Pradesh", cities: ["Lucknow", "Kanpur", "Agra", "Varanasi", "Noida"] },
  { state: "Uttarakhand", cities: ["Dehradun", "Haridwar", "Roorkee", "Nainital", "Rishikesh"] },
  { state: "West Bengal", cities: ["Kolkata", "Howrah", "Darjeeling", "Siliguri", "Asansol"] }
];

// --- CUSTOM SEARCHABLE DROPDOWN COMPONENT ---
const SearchableSelect = ({ name, value, options, onChange, placeholder, disabled, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef(null);
 
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
 
  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );
 
  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <input
          type="text"
          disabled={disabled}
          value={isOpen ? searchTerm : value}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onClick={() => setIsOpen(true)}
          placeholder={value || placeholder}
          className={`w-full px-4 py-3 pr-10 rounded-xl border outline-none font-medium transition-colors focus:ring-2 focus:ring-indigo-500
            ${error
              ? 'border-red-400 bg-red-50 dark:bg-red-900/20 dark:border-red-700'
              : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'
            }
            text-gray-900 dark:text-slate-100
            placeholder:text-gray-400 dark:placeholder:text-slate-500
            disabled:bg-gray-100 dark:disabled:bg-slate-700/50
            disabled:text-gray-400 dark:disabled:text-slate-500
            disabled:cursor-not-allowed`}
        />
        <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-500 dark:text-slate-400">
          <svg className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7"></path>
          </svg>
        </div>
      </div>
 
      {isOpen && !disabled && (
        <ul className="absolute z-[9999] w-full mt-1
          bg-white dark:bg-slate-800
          border border-gray-100 dark:border-slate-600
          rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.2)]
          max-h-60 overflow-y-auto
          animate-in fade-in zoom-in-95 duration-100 no-scrollbar"
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <li
                key={option}
                onClick={() => {
                  onChange({ target: { name, value: option } });
                  setSearchTerm("");
                  setIsOpen(false);
                }}
                className={`px-4 py-2.5 cursor-pointer text-sm font-semibold transition-colors
                  ${value === option
                    ? 'bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400'
                    : 'text-gray-700 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-slate-700 hover:text-indigo-700 dark:hover:text-indigo-400'
                  }`}
              >
                {option}
              </li>
            ))
          ) : (
            <li className="px-4 py-3 text-sm font-medium text-gray-500 dark:text-slate-400 text-center">
              No results found
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

const Profile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [successMessage, setSuccessMessage] = useState("");
  const [globalAlert, setGlobalAlert] = useState({ isOpen: false, title: "", message: "" });
  
  const [errors, setErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referralError, setReferralError] = useState("");
  const inviteLink = `https://rkvisionindia.com/register/${referralCode}`; 
  
  const fileInputRef = useRef(null);
  const [selectedFileBlob, setSelectedFileBlob] = useState(null);
  
  const [previewImage, setPreviewImage] = useState(null);
  const [initialData, setInitialData] = useState(null);
  
  const [formData, setFormData] = useState({
    id: "", name: "", email: "", phone: "", address: "", address2: "", 
    city: "", state: "", pincode: "", refer: "", profile: null,
    kyc: "none", membership: "prospect"
  });

  const [passwordData, setPasswordData] = useState({
    password: "", confirmPassword: ""
  });


  // --- NEW: BANK DETAILS STATE ---
  const bankProofRef = useRef(null);
  const [submittingBank, setSubmittingBank] = useState(false);
  const [bankStatus, setBankStatus] = useState('loading'); 
  const [bankData, setBankData] = useState(null);
  const [showResubmitBank, setShowResubmitBank] = useState(false);
  const [viewBankProof, setViewBankProof] = useState(false);
  const [isBankSuccessOpen, setIsBankSuccessOpen] = useState(false);
  const [bankErrors, setBankErrors] = useState({});
  const [selectedBankProofBlob, setSelectedBankProofBlob] = useState(null);
  
  const [bankForm, setBankForm] = useState({
    bank: "", account: "", confirmAccount: "", holder: "", 
    ifsc: "", branch: "", address: "", proofPreview: null
  });

  // --- NEW: DEDICATED KYC STATE ---
  const [kycStatus, setKycStatus] = useState('loading'); 

  // --- 1. MERGED DATA LOAD (PROFILE + KYC + BANK) ---
  // --- 1. MERGED DATA LOAD (PROFILE + KYC + BANK + REFERRAL) ---
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        // Fetch Profile, KYC, and Referral Code simultaneously
        // FIX: Ensure referRes is declared right here in the brackets!
        const [profileRes, kycRes, referRes] = await Promise.allSettled([
          axiosInstance.get('/profile/get'),
          axiosInstance.get('/kyc/get'),
          axiosInstance.get('/user/refer')
        ]);

        // 1. Process Profile Data
        if (profileRes.status === 'fulfilled' && profileRes.value && profileRes.value.data) {
          const pData = profileRes.value.data;
          const userId = pData.id || "";
          const imageUrl = userId ? `${axiosInstance.defaults.baseURL}/profile/get-image/${userId}?t=${Date.now()}` : null;

          const fetchedProfile = {
            id: userId,
            name: pData.name || "",
            email: pData.email || "",
            phone: pData.phone || "",
            address: pData.address || "",
            address2: pData.address2 || "", 
            city: pData.city || "",
            state: pData.state || "",
            pincode: pData.pincode || "",
            refer: pData.refer || "",
            profile: imageUrl, 
            membership: pData.membership || "prospect"
          };
          setFormData(fetchedProfile);
          setInitialData(fetchedProfile); 
        }

        // 2. Process Real-Time KYC Data
        let currentKycStatus = 'none';
        if (kycRes.status === 'fulfilled' && kycRes.value && kycRes.value.data) {
          currentKycStatus = String(kycRes.value.data.status).toLowerCase();
          setKycStatus(currentKycStatus);
        } else {
          setKycStatus('none');
        }

        // 3. Process Bank Details (ONLY if KYC is strictly 'approved')
        if (currentKycStatus === 'approved') {
          try {
            const bankRes = await axiosInstance.get('/bank/get');
            if (bankRes.data && bankRes.data.status) {
              const currentBankStatus = String(bankRes.data.status).toLowerCase();
              setBankStatus(currentBankStatus);
              setBankData(bankRes.data);
              
              // PRE-FILL FORM
              if (currentBankStatus !== 'none') {
                
                // SECURE IMAGE FETCHING
                let fetchedProofUrl = null;
                try {
                  const proofRes = await axiosInstance.get('/bank/proof', { responseType: 'blob' });
                  if (proofRes.data) fetchedProofUrl = URL.createObjectURL(proofRes.data);
                } catch (imgErr) {
                  console.error("Failed to load proof image securely.");
                }

                setBankForm(prev => ({
                  ...prev,
                  bank: bankRes.data.bank || "",
                  account: bankRes.data.account || "",
                  confirmAccount: bankRes.data.account || "",
                  holder: bankRes.data.holder || "",
                  ifsc: bankRes.data.ifsc || "",
                  branch: bankRes.data.branch || "",
                  address: bankRes.data.address || "",
                  proofPreview: fetchedProofUrl 
                }));
              }
            } else {
              setBankStatus('none');
            }
          } catch (err) {
            setBankStatus('none'); 
          }
        } else {
          setBankStatus('locked'); // Lock bank if KYC isn't approved yet
        }

        // 4. Process Referral Code Data
        if (referRes.status === 'fulfilled' && referRes.value && referRes.value.data) {
          setReferralCode(referRes.value.data.refer || "");
          setReferralError("");
        } else if (referRes.status === 'rejected') {
          // If the API throws an error (e.g., unpaid fees), trap the error message
          const errMsg = referRes.reason?.response?.data?.error || "Referral code locked. You must have at least one approved payment to share your code.";
          setReferralError(errMsg);
        }

      } catch (err) {
        setGlobalAlert({ isOpen: true, title: "Connection Error", message: "Failed to load dashboard data." });
      } finally {
        setLoading(false);
      }
    };
    
    fetchAllData();
  }, []);
  // --- 2. GHOST UPDATE PREVENTION (ADDED ADDRESS2) ---
  const isDirty = JSON.stringify({
    name: formData.name, email: formData.email, address: formData.address, address2: formData.address2,
    city: formData.city, state: formData.state, pincode: formData.pincode
  }) !== JSON.stringify({
    name: initialData?.name, email: initialData?.email, address: initialData?.address, address2: initialData?.address2,
    city: initialData?.city, state: initialData?.state, pincode: initialData?.pincode
  });

  // --- 3. HIGH-LEVEL VALIDATION ENGINE ---
  const validateField = (name, value, currentDataObj = formData) => {
    if (name !== 'address2' && value.trim() === "") return ""; // Address 2 is optional

    let errorMsg = "";
    switch (name) {
      case "name":
        if (!/^[a-zA-Z\s]+$/.test(value)) errorMsg = "Name cannot contain numbers or special characters.";
        else if (value.trim().length <= 3) errorMsg = "Name must be more than 3 characters long.";
        break;
      case "email":
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) errorMsg = "Enter a valid email address.";
        break;
      case "address":
        if (value.trim().length < 5) errorMsg = "Please enter a complete address.";
        break;
      case "state":
        const stateExists = locationData.some(item => item.state.toLowerCase() === value.toLowerCase());
        if (!stateExists) errorMsg = "Please select a valid state from the list.";
        break;
      case "city":
        const cityExists = locationData.some(item => item.cities.some(c => c.toLowerCase() === value.toLowerCase()));
        if (!cityExists) errorMsg = "Please select a valid city from the list.";
        break;
      case "pincode":
        if (!/^\d{6}$/.test(value)) errorMsg = "PIN code must be exactly 6 digits.";
        break;
      case "password":
        if (!/^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(value)) {
          errorMsg = "Must be 8+ chars with 1 uppercase, 1 number, and 1 symbol.";
        }
        if (currentDataObj.confirmPassword && value !== currentDataObj.confirmPassword) {
          setPasswordErrors(prev => ({ ...prev, confirmPassword: "Passwords do not match." }));
        } else if (currentDataObj.confirmPassword && value === currentDataObj.confirmPassword) {
          setPasswordErrors(prev => ({ ...prev, confirmPassword: "" }));
        }
        break;
      case "confirmPassword":
        if (value !== currentDataObj.password) errorMsg = "Passwords do not match.";
        break;
      default:
        break;
    }
    return errorMsg;
  };

 
  const validateProfileForm = () => {
    let newErrors = {};
    const fieldsToValidate = ['name', 'email', 'address', 'city', 'state', 'pincode'];
    let isValid = true;

    fieldsToValidate.forEach(field => {
      if (formData[field].trim() === "") {
        newErrors[field] = "This field is required.";
        isValid = false;
      } else {
        const fieldError = validateField(field, formData[field]);
        if (fieldError) {
          newErrors[field] = fieldError;
          isValid = false;
        }
      }
    });
    setErrors(newErrors);
    return isValid;
  };

  const validatePasswordForm = () => {
    let newErrors = {};
    let isValid = true;

    if (passwordData.password.trim() === "") {
      newErrors.password = "New password is required."; isValid = false;
    } else {
      const passError = validateField("password", passwordData.password, passwordData);
      if (passError) { newErrors.password = passError; isValid = false; }
    }

    if (passwordData.confirmPassword.trim() === "") {
      newErrors.confirmPassword = "Confirm password is required."; isValid = false;
    } else {
      const confirmError = validateField("confirmPassword", passwordData.confirmPassword, passwordData);
      if (confirmError) { newErrors.confirmPassword = confirmError; isValid = false; }
    }

    setPasswordErrors(newErrors);
    return isValid;
  };

   // --- INVITE BUTTON HANDLER ---
   const handleInviteClick = () => {
    if (referralError) {
      // Backend threw an error (e.g. KYC not approved), so show the alert
      setGlobalAlert({ 
        isOpen: true, 
        title: "Action Required", 
        message: referralError // This directly passes the backend's exact message!
      });
    } else {
      // No error, open the share modal
      setIsInviteModalOpen(true);
    }
  };
  // --- 4. INPUT HANDLERS ---
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    const finalValue = name === 'pincode' ? value.replace(/\D/g, '') : value;
    
    setFormData(prev => {
      const updated = { ...prev, [name]: finalValue };
      if (name === "state") updated.city = ""; // Clear city if state changes
      return updated;
    });
    
    const fieldError = validateField(name, finalValue);
    if (fieldError) setErrors(prev => ({ ...prev, [name]: fieldError }));
    else setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    const newData = { ...passwordData, [name]: value };
    const fieldError = validateField(name, value, newData);
    if (fieldError) setPasswordErrors(prev => ({ ...prev, [name]: fieldError }));
    else setPasswordErrors(prev => ({ ...prev, [name]: "" }));
  };

  const availableCities = locationData.find(s => s.state === formData.state)?.cities || [];

  // --- 5. IMAGE ACTIONS ---
  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // We can keep the 2MB initial limit so users can still upload standard photos,
    // but the compressor below will shrink it down to 500KB for the database.
    if (file.size > 2097152) { 
      setGlobalAlert({ isOpen: true, title: "File Too Large", message: "Profile picture must be smaller than 2MB." });
      return;
    }
    
    try {
      // CHANGED: maxSizeMB is now 0.5 (500KB)
      const options = { 
        maxSizeMB: 0.5, 
        maxWidthOrHeight: 512, 
        useWebWorker: true, 
        fileType: 'image/jpeg' 
      };
      
      // Compress the file (Returns a Blob/File)
      const compressedFile = await imageCompression(file, options);
      
      // Store the actual file blob to send to the API later
      setSelectedFileBlob(compressedFile);
      
      // Create a temporary local URL to show the user a preview instantly
      setPreviewImage(URL.createObjectURL(compressedFile)); 
    } catch (error) {
      setGlobalAlert({ isOpen: true, title: "Processing Failed", message: "Failed to optimize the image file." });
    }
  };

  const handleUploadPhoto = async () => {
    if (!selectedFileBlob) return;
    setUploadingPhoto(true);
    
    try {
      // Create FormData and append the compressed image bytes
      const uploadData = new FormData();
      uploadData.append('profile', selectedFileBlob);

      // CHANGED: Using .put instead of .post to match your backend API
      await axiosInstance.put('/profile/set-photo', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Update the main profile image to force a reload from the server using cache-busting
      const newImageUrl = `${axiosInstance.defaults.baseURL}/profile/get-image/${formData.id}?t=${Date.now()}`;
      
      setFormData(prev => ({ ...prev, profile: newImageUrl }));
      setInitialData(prev => ({ ...prev, profile: newImageUrl }));
      
      // Clear previews and blobs
      setPreviewImage(null);
      setSelectedFileBlob(null);
      
      setSuccessMessage("Profile photo uploaded successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      setGlobalAlert({ isOpen: true, title: "Upload Failed", message: error.response?.data?.message || "Failed to upload photo." });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    try {
      // CHANGED: We are now sending a DELETE request instead of POST
      await axiosInstance.delete('/profile/remove-photo');
      
      setFormData(prev => ({ ...prev, profile: placeholderImageBase64 }));
      setInitialData(prev => ({ ...prev, profile: placeholderImageBase64 }));
      setPreviewImage(null);
      setSelectedFileBlob(null);
      if (fileInputRef.current) fileInputRef.current.value = ""; 
      
      setSuccessMessage("Profile photo removed successfully.");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      setGlobalAlert({ isOpen: true, title: "Action Failed", message: "Failed to remove photo." });
    }
  };

  // --- 6. FORM SUBMISSIONS ---
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!isDirty) return; 
    
    if (!validateProfileForm()) {
      setGlobalAlert({ isOpen: true, title: "Validation Error", message: "Please fix the errors in the form before saving." });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formData.name, email: formData.email, address: formData.address,
        address2: formData.address2, // Sent actual address2
        city: formData.city, state: formData.state, pincode: formData.pincode
      };
      await axiosInstance.post('/profile/set', payload);
      setSuccessMessage("Profile details updated successfully!");
      setInitialData(formData); 
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setGlobalAlert({ isOpen: true, title: "Update Failed", message: err.response?.data?.message || "Failed to update profile details." });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validatePasswordForm()) {
      setGlobalAlert({ isOpen: true, title: "Validation Error", message: "Please fix the password errors before saving." });
      return;
    }
    setUpdatingPassword(true);
    try {
      await axiosInstance.post('/profile/update-password', { password: passwordData.password });
      setSuccessMessage("Password updated securely!");
      setPasswordData({ password: "", confirmPassword: "" }); 
      setPasswordErrors({});
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setGlobalAlert({ isOpen: true, title: "Update Failed", message: err.response?.data?.message || "Failed to change password." });
    } finally {
      setUpdatingPassword(false);
    }
  };

  const displayImage = previewImage || formData.profile;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // ==========================================
  // --- NEW: BANK VALIDATION & SUBMISSION ---
  // ==========================================
  const validateBankField = (name, value, currentForm) => {
    let errorMsg = "";
    if (value.trim() === "") return "";
    switch (name) {
      case "bank": if (!/^[a-zA-Z\s\-&]+$/.test(value)) errorMsg = "Enter a valid bank name."; break;
      case "account":
        if (!/^\d{9,18}$/.test(value)) errorMsg = "Account must be 9-18 digits.";
        if (currentForm.confirmAccount && value !== currentForm.confirmAccount) setBankErrors(prev => ({ ...prev, confirmAccount: "Accounts do not match." }));
        else if (currentForm.confirmAccount && value === currentForm.confirmAccount) setBankErrors(prev => ({ ...prev, confirmAccount: "" }));
        break;
      case "confirmAccount": if (value !== currentForm.account) errorMsg = "Accounts do not match."; break;
      case "ifsc": if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value)) errorMsg = "Invalid IFSC format."; break;
      case "holder": if (!/^[a-zA-Z\s]+$/.test(value)) errorMsg = "Name can only contain letters."; break;
      default: break;
    }
    return errorMsg;
  };

  const handleBankTextChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;
    if (name === 'account' || name === 'confirmAccount') finalValue = value.replace(/\D/g, '').substring(0, 18);
    if (name === 'ifsc') finalValue = value.toUpperCase().substring(0, 11);
    
    const updatedForm = { ...bankForm, [name]: finalValue };
    setBankForm(updatedForm);
    setBankErrors(prev => ({ ...prev, [name]: validateBankField(name, finalValue, updatedForm) }));
  };

  const handleBankProofUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2097152) {
      setGlobalAlert({ isOpen: true, title: "File Too Large", message: "Proof must be under 2MB." });
      return;
    }
    try {
      const options = { maxSizeMB: 0.5, maxWidthOrHeight: 1024, useWebWorker: true, fileType: 'image/jpeg' };
      const compressedFile = await imageCompression(file, options);
      setSelectedBankProofBlob(compressedFile);
      setBankForm(prev => ({ ...prev, proofPreview: URL.createObjectURL(compressedFile) }));
      setBankErrors(prev => ({ ...prev, proofPreview: "" }));
    } catch (error) {
      setGlobalAlert({ isOpen: true, title: "Error", message: "Failed to process image." });
    }
  };

  const isBankFormValid = bankForm.bank && bankForm.account && bankForm.account === bankForm.confirmAccount && 
                          bankForm.holder && bankForm.ifsc.length === 11 && bankForm.branch && bankForm.address && 
                          selectedBankProofBlob && Object.values(bankErrors).every(err => err === "");

  const handleBankSubmit = async (e) => {
    e.preventDefault();
    if (!isBankFormValid) return;
    setSubmittingBank(true);
    try {
      const uploadData = new FormData();
      uploadData.append('bank', bankForm.bank);
      uploadData.append('account', bankForm.account);
      uploadData.append('holder', bankForm.holder);
      uploadData.append('ifsc', bankForm.ifsc);
      uploadData.append('branch', bankForm.branch);
      uploadData.append('address', bankForm.address);
      uploadData.append('proof', selectedBankProofBlob); 

      await axiosInstance.post('/bank/submit', uploadData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBankData({ ...bankForm, status: "pending", submitted_on: new Date().toISOString() });
      setBankStatus('pending');
      setShowResubmitBank(false);
      setIsBankSuccessOpen(true); 
    } catch (err) {
      setGlobalAlert({ isOpen: true, title: "Submission Failed", message: err.response?.data?.message || "Failed to submit Bank details." });
    } finally {
      setSubmittingBank(false);
    }
  };



return (
    <div className="w-full px-3 sm:px-6 lg:px-24 space-y-6 pb-10">

      {/* FLOATING SUCCESS TOAST */}
      {successMessage && (
        <div className="fixed top-24 right-6 z-50 bg-white dark:bg-slate-800 border border-green-200 dark:border-green-700 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] rounded-2xl p-4 flex items-start gap-4 animate-in slide-in-from-right-8 fade-in duration-300">
          <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
          </div>
          <div className="mr-4 mt-1">
            <h4 className="text-sm font-bold text-gray-900 dark:text-slate-100 leading-none mb-1">Success</h4>
            <p className="text-xs font-semibold text-gray-500 dark:text-slate-400">{successMessage}</p>
          </div>
          <button onClick={() => setSuccessMessage("")} className="text-gray-400 dark:text-slate-500 hover:text-gray-700 dark:hover:text-slate-300 transition-colors p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>
      )}

      {/* Header & Breadcrumbs */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/dashboard" className="inline-flex items-center text-sm font-bold text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path></svg>
                Dashboard
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-gray-400 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                <span className="ml-1 text-sm font-bold text-gray-900 dark:text-slate-200 md:ml-2">My Profile</span>
              </div>
            </li>
          </ol>
        </nav>
        <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-amber-100 to-amber-50 dark:from-amber-900/30 dark:to-amber-800/20 border border-amber-200 dark:border-amber-700/50 shadow-sm w-fit">
          <span className="w-2 h-2 rounded-full bg-amber-500 mr-2 animate-pulse"></span>
          <span className="text-sm font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider">{formData.membership}</span>
        </div>
      </div>

      {/* ── PERSONAL DETAILS CARD ─────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-700">
        <div className="p-6 sm:p-10">
          <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-8 border-b border-gray-100 dark:border-slate-700 pb-4">Personal Details</h2>
          <form onSubmit={handleProfileSubmit}>

            {/* PROFILE PICTURE SECTION */}
            <div className="flex flex-col sm:flex-row items-center gap-8 mb-10">
              <div className="relative group cursor-pointer" onClick={() => fileInputRef.current.click()} title="Select a new photo">
                <div className="w-36 h-36 rounded-full bg-indigo-50 dark:bg-slate-700 border-4 border-white dark:border-slate-600 shadow-xl overflow-hidden flex items-center justify-center relative transition-transform duration-300 group-hover:scale-[1.02]">
                  <img
                    src={displayImage}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = placeholderImageBase64;
                    }}
                  />
                  <div className="absolute inset-0 bg-indigo-900/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path></svg>
                  </div>
                </div>
                <input type="file" ref={fileInputRef} onChange={handleImageSelect} accept="image/jpeg, image/png, image/webp" className="hidden" />
              </div>

              <div className="text-center sm:text-left flex flex-col items-center sm:items-start">
                <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100">Profile Photo</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400 mt-1 mb-4 max-w-xs leading-relaxed">JPEG, PNG, or WEBP up to 2MB. Click the image to select a file.</p>
                <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
                  <button type="button" disabled={!selectedFileBlob || uploadingPhoto} onClick={handleUploadPhoto} className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm text-white shadow-md transition-all disabled:bg-gray-300 dark:disabled:bg-slate-600 disabled:shadow-none bg-indigo-600 hover:bg-indigo-700 active:scale-95">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path></svg>
                    {uploadingPhoto ? "Uploading..." : "Upload Photo"}
                  </button>
                  <button type="button" disabled={(formData.profile === placeholderImageBase64) && !previewImage} onClick={handleRemovePhoto} className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm text-red-600 dark:text-red-400 border-2 border-red-100 dark:border-red-800/50 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-95">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                    Remove
                  </button>
                </div>
              </div>
            </div>

            {/* FORM GRID */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6">

              <div className="lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Full Name</label>
                <input type="text" name="name" value={formData.name} onChange={handleProfileChange}
                  className={`w-full px-4 py-3 rounded-xl border ${errors.name ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'} focus:ring-2 focus:ring-indigo-500 outline-none font-medium text-gray-900 dark:text-slate-100 transition-colors`} />
                {errors.name && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.name}</p>}
              </div>

              <div className="lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Email Address</label>
                <input type="email" name="email" value={formData.email} onChange={handleProfileChange}
                  className={`w-full px-4 py-3 rounded-xl border ${errors.email ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'} focus:ring-2 focus:ring-indigo-500 outline-none font-medium text-gray-900 dark:text-slate-100 transition-colors`} />
                {errors.email && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.email}</p>}
              </div>

              <div className="lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Phone Number</label>
                <input type="text" value={`+91 ${formData.phone}`} readOnly
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-100 dark:bg-slate-700/50 text-gray-500 dark:text-slate-400 cursor-not-allowed outline-none font-medium" />
              </div>

              <div className="md:col-span-2 lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Address Line 1</label>
                  <input type="text" name="address" value={formData.address} onChange={handleProfileChange} placeholder="Street address or P.O. Box"
                    className={`w-full px-4 py-3 rounded-xl border ${errors.address ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'} focus:ring-2 focus:ring-indigo-500 outline-none font-medium text-gray-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 transition-colors`} />
                  {errors.address && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.address}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Address Line 2 <span className="text-gray-400 dark:text-slate-500 font-normal">(Optional)</span></label>
                  <input type="text" name="address2" value={formData.address2} onChange={handleProfileChange} placeholder="Apt, suite, unit, building, etc."
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-indigo-500 outline-none font-medium text-gray-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 transition-colors" />
                </div>
              </div>

              <div className="lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">State</label>
                <SearchableSelect
                  name="state" value={formData.state}
                  options={locationData.map(item => item.state)}
                  onChange={handleProfileChange}
                  placeholder="Type to search state..."
                  error={errors.state}
                />
                {errors.state && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.state}</p>}
              </div>

              <div className="lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">City</label>
                <SearchableSelect
                  name="city" value={formData.city}
                  options={availableCities}
                  onChange={handleProfileChange}
                  placeholder="Type to search city..."
                  disabled={!formData.state}
                  error={errors.city}
                />
                {errors.city && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.city}</p>}
              </div>

              <div className="lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">PIN Code</label>
                <input type="text" name="pincode" value={formData.pincode} onChange={handleProfileChange} maxLength="6"
                  className={`w-full px-4 py-3 rounded-xl border ${errors.pincode ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'} focus:ring-2 focus:ring-indigo-500 outline-none font-medium text-gray-900 dark:text-slate-100 transition-colors`} />
                {errors.pincode && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.pincode}</p>}
              </div>
            </div>

            {/* ACTION BUTTONS */}
            <div className="mt-10 flex flex-col sm:flex-row justify-center items-center gap-4 border-t border-gray-100 dark:border-slate-700 pt-8">
              {/* Only show the Invite button if KYC is fully approved */}
             
                <button
                  type="button"
                  onClick={handleInviteClick}
                  className="w-full sm:w-auto px-8 py-3.5 rounded-xl font-bold text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 border border-indigo-200 dark:border-indigo-700/50 transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"></path></svg>
                  Grow Your Network
                </button>
             
              <button
                type="submit"
                disabled={!isDirty || saving}
                className={`w-full sm:w-auto px-10 py-3.5 rounded-xl font-bold text-white shadow-lg transition-all duration-300 ${
                  !isDirty
                    ? 'bg-gray-300 dark:bg-slate-600 cursor-not-allowed shadow-none'
                    : saving
                      ? 'bg-indigo-400 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] hover:shadow-indigo-200'
                }`}
              >
                {!isDirty ? "No Changes to Save" : saving ? "Saving Profile..." : "Update Details"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ── SECURITY / PASSWORD CARD ──────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-700 overflow-hidden mt-8">
        <div className="p-6 sm:p-10">
          <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-8 border-b border-gray-100 dark:border-slate-700 pb-4">Security Settings</h2>

          <form onSubmit={handlePasswordSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-6 items-end">

              <div className="lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">New Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={passwordData.password}
                    onChange={handlePasswordChange}
                    className={`w-full px-4 py-3 pr-12 rounded-xl border ${passwordErrors.password ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'} focus:ring-2 focus:ring-indigo-500 outline-none font-medium text-gray-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 transition-colors`}
                    placeholder="Enter new password"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors focus:outline-none">
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    )}
                  </button>
                </div>
                {passwordErrors.password && <p className="text-red-500 text-xs mt-1.5 font-medium absolute">{passwordErrors.password}</p>}
              </div>

              <div className="lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Confirm New Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className={`w-full px-4 py-3 pr-12 rounded-xl border ${passwordErrors.confirmPassword ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'} focus:ring-2 focus:ring-indigo-500 outline-none font-medium text-gray-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 transition-colors`}
                    placeholder="Confirm new password"
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors focus:outline-none">
                    {showConfirmPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    )}
                  </button>
                </div>
                {passwordErrors.confirmPassword && <p className="text-red-500 text-xs mt-1.5 font-medium absolute">{passwordErrors.confirmPassword}</p>}
              </div>

              <div className="lg:col-span-1">
                <button
                  type="submit"
                  disabled={updatingPassword || !passwordData.password || !passwordData.confirmPassword}
                  className={`w-full py-3.5 rounded-xl font-bold text-white shadow-lg transition-all duration-300 ${
                    (!passwordData.password || !passwordData.confirmPassword)
                      ? 'bg-gray-300 dark:bg-slate-600 cursor-not-allowed shadow-none'
                      : updatingPassword
                        ? 'bg-indigo-400 cursor-not-allowed'
                        : 'bg-gray-900 dark:bg-slate-200 dark:text-slate-900 hover:bg-gray-800 dark:hover:bg-white active:scale-[0.98]'
                  }`}
                >
                  {updatingPassword ? "Updating..." : "Update Password"}
                </button>
              </div>

            </div>
          </form>
        </div>
      </div>

      {/* ── BANK DETAILS CARD ─────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-700 overflow-hidden relative mt-8">
        <div className="p-6 sm:p-10 relative">
          <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-8 border-b border-gray-100 dark:border-slate-700 pb-4">Bank & Payout Details</h2>

          {/* STATE 1: KYC LOCKED */}
          {kycStatus !== "approved" && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-8 opacity-20 blur-[3px] pointer-events-none select-none">
                <div><label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Bank Name</label><div className="w-full h-12 bg-gray-200 dark:bg-slate-600 rounded-xl"></div></div>
                <div><label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Account Holder Name</label><div className="w-full h-12 bg-gray-200 dark:bg-slate-600 rounded-xl"></div></div>
                <div><label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Account Number</label><div className="w-full h-12 bg-gray-200 dark:bg-slate-600 rounded-xl"></div></div>
                <div><label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">IFSC Code</label><div className="w-full h-12 bg-gray-200 dark:bg-slate-600 rounded-xl"></div></div>
              </div>
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/50 dark:bg-slate-800/60 backdrop-blur-sm rounded-3xl">
                <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 rounded-full flex items-center justify-center mb-4 shadow-sm border border-amber-200 dark:border-amber-700/50">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                </div>
                <h3 className="text-xl font-extrabold text-gray-900 dark:text-slate-100 mb-2">KYC Verification Required</h3>
                <p className="text-sm text-gray-600 dark:text-slate-400 text-center max-w-md mb-6 font-medium leading-relaxed">To comply with financial regulations and enable commission payouts, you must complete your identity verification first.</p>
                <Link to="/dashboard/kyc" className="px-8 py-3.5 bg-gray-900 dark:bg-slate-200 dark:text-slate-900 text-white font-bold rounded-xl hover:bg-gray-800 dark:hover:bg-white transition-colors shadow-lg shadow-gray-200/50 dark:shadow-slate-900/50 active:scale-[0.98]">
                  {formData.kyc === 'pending' ? "Check KYC Status" : "Complete KYC Now"}
                </Link>
              </div>
            </>
          )}

          {/* STATE 2: KYC APPROVED */}
          {kycStatus === 'approved' && (() => {
            const isBankReadOnly = bankStatus === 'pending' || bankStatus === 'approved';

            return (
              <>
                {/* Status Banners */}
                {bankStatus === 'pending' && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-xl p-5 mb-8 flex items-start gap-4">
                    <div className="w-10 h-10 bg-white dark:bg-slate-700 border border-amber-200 dark:border-amber-700 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm text-amber-500">
                      <svg className="w-6 h-6 animate-spin-slow" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-amber-900 dark:text-amber-300">Verification Pending</h3>
                      <p className="text-sm text-amber-700 dark:text-amber-400 mt-1 font-medium">Your details are currently under review by the administration team. The form is locked during this process.</p>
                    </div>
                  </div>
                )}

                {bankStatus === 'approved' && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700/50 rounded-xl p-5 mb-8 flex items-start gap-4">
                    <div className="w-10 h-10 bg-white dark:bg-slate-700 border border-green-200 dark:border-green-700 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm text-green-600 dark:text-green-400">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-green-900 dark:text-green-300">Bank Details Approved</h3>
                      <p className="text-sm text-green-700 dark:text-green-400 mt-1 font-medium">Your bank account has been successfully verified and is active for payouts.</p>
                    </div>
                  </div>
                )}

                {bankStatus === 'rejected' && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50 rounded-xl p-5 mb-8 flex items-start gap-4">
                    <div className="w-10 h-10 bg-white dark:bg-slate-700 border border-red-200 dark:border-red-700 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm text-red-600 dark:text-red-400">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-red-900 dark:text-red-300">Verification Rejected</h3>
                      <p className="text-sm text-red-700 dark:text-red-400 mt-1 font-medium">Your submission was rejected. Admin Remark: <span className="font-bold">{bankData?.remarks || "Details mismatched with proof."}</span> Please correct your details below and resubmit.</p>
                    </div>
                  </div>
                )}

                {/* BANK FORM */}
                {(bankStatus === 'none' || bankStatus === 'rejected' || isBankReadOnly) && (
                  <form onSubmit={handleBankSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-x-6 gap-y-6">

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Bank Name {isBankReadOnly ? '' : '*'}</label>
                        <input required={!isBankReadOnly} type="text" name="bank" value={bankForm.bank} onChange={handleBankTextChange}
                          placeholder={isBankReadOnly ? "" : "e.g. HDFC Bank"}
                          readOnly={isBankReadOnly}
                          className={`w-full px-4 py-3 rounded-xl border ${bankErrors.bank ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-slate-600'} ${isBankReadOnly ? 'bg-gray-50 dark:bg-slate-700/50 text-gray-500 dark:text-slate-400 cursor-not-allowed font-bold' : 'bg-white dark:bg-slate-700 focus:ring-2 focus:ring-indigo-500 font-medium text-gray-900 dark:text-slate-100'} outline-none transition-colors`} />
                        {bankErrors.bank && <p className="text-red-500 text-xs mt-1.5 font-medium">{bankErrors.bank}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Branch Name {isBankReadOnly ? '' : '*'}</label>
                        <input required={!isBankReadOnly} type="text" name="branch" value={bankForm.branch} onChange={handleBankTextChange}
                          placeholder={isBankReadOnly ? "" : "e.g. MG Road Branch"}
                          readOnly={isBankReadOnly}
                          className={`w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-slate-600 ${isBankReadOnly ? 'bg-gray-50 dark:bg-slate-700/50 text-gray-500 dark:text-slate-400 cursor-not-allowed font-bold' : 'bg-white dark:bg-slate-700 focus:ring-2 focus:ring-indigo-500 font-medium text-gray-900 dark:text-slate-100'} outline-none transition-colors`} />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Account Holder Name {isBankReadOnly ? '' : '*'}</label>
                        <input required={!isBankReadOnly} type="text" name="holder" value={bankForm.holder} onChange={handleBankTextChange}
                          placeholder={isBankReadOnly ? "" : "As per passbook"}
                          readOnly={isBankReadOnly}
                          className={`w-full px-4 py-3 rounded-xl border ${bankErrors.holder ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-slate-600'} ${isBankReadOnly ? 'bg-gray-50 dark:bg-slate-700/50 text-gray-500 dark:text-slate-400 cursor-not-allowed font-bold' : 'bg-white dark:bg-slate-700 focus:ring-2 focus:ring-indigo-500 font-medium text-gray-900 dark:text-slate-100'} outline-none transition-colors`} />
                        {bankErrors.holder && <p className="text-red-500 text-xs mt-1.5 font-medium">{bankErrors.holder}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">IFSC Code {isBankReadOnly ? '' : '*'}</label>
                        <input required={!isBankReadOnly} type="text" name="ifsc" value={bankForm.ifsc} onChange={handleBankTextChange}
                          placeholder={isBankReadOnly ? "" : "e.g. HDFC0001234"}
                          readOnly={isBankReadOnly}
                          className={`w-full px-4 py-3 rounded-xl border ${bankErrors.ifsc ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-slate-600'} ${isBankReadOnly ? 'bg-gray-50 dark:bg-slate-700/50 text-gray-500 dark:text-slate-400 cursor-not-allowed font-bold' : 'bg-white dark:bg-slate-700 focus:ring-2 focus:ring-indigo-500 font-medium text-gray-900 dark:text-slate-100'} outline-none uppercase transition-colors`} />
                        {bankErrors.ifsc && <p className="text-red-500 text-xs mt-1.5 font-medium">{bankErrors.ifsc}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Account Number {isBankReadOnly ? '' : '*'}</label>
                        {isBankReadOnly ? (
                          <input type="text" value={`XXXXXXXX${bankForm.account?.slice(-4) || ''}`} readOnly
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/50 text-gray-500 dark:text-slate-400 cursor-not-allowed font-mono outline-none" />
                        ) : (
                          <input required type="password" name="account" value={bankForm.account} onChange={handleBankTextChange} placeholder="Enter Account Number"
                            className={`w-full px-4 py-3 rounded-xl border ${bankErrors.account ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-indigo-500'} outline-none font-medium text-gray-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 transition-colors`} />
                        )}
                        {bankErrors.account && <p className="text-red-500 text-xs mt-1.5 font-medium">{bankErrors.account}</p>}
                      </div>

                      {!isBankReadOnly ? (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Re-enter Account Number *</label>
                          <input required type="text" name="confirmAccount" value={bankForm.confirmAccount} onChange={handleBankTextChange} placeholder="Confirm Account Number"
                            className={`w-full px-4 py-3 rounded-xl border ${bankErrors.confirmAccount ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-indigo-500'} outline-none font-medium text-gray-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 transition-colors`} />
                          {bankErrors.confirmAccount && <p className="text-red-500 text-xs mt-1.5 font-medium">{bankErrors.confirmAccount}</p>}
                        </div>
                      ) : (
                        <div className="hidden md:block"></div>
                      )}

                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Branch Address {isBankReadOnly ? '' : '*'}</label>
                        <input required={!isBankReadOnly} type="text" name="address" value={bankForm.address} onChange={handleBankTextChange}
                          placeholder={isBankReadOnly ? "" : "Enter full branch address"}
                          readOnly={isBankReadOnly}
                          className={`w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-slate-600 ${isBankReadOnly ? 'bg-gray-50 dark:bg-slate-700/50 text-gray-500 dark:text-slate-400 cursor-not-allowed font-bold' : 'bg-white dark:bg-slate-700 focus:ring-2 focus:ring-indigo-500 font-medium text-gray-900 dark:text-slate-100'} outline-none transition-colors`} />
                      </div>
                    </div>

                    {/* Proof Upload Row */}
                    <div className="mt-8 pt-2">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gray-50/50 dark:bg-slate-700/30 border border-gray-200 dark:border-slate-600 rounded-xl p-4 sm:p-5">
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 dark:text-slate-200">Bank Proof (Passbook or Statement) {isBankReadOnly ? '' : '*'}</label>
                          {!isBankReadOnly && <p className="text-xs font-medium text-gray-500 dark:text-slate-400 mt-1">Clear photo of account holder name & numbers</p>}
                        </div>
                        <div className="flex items-center gap-3">
                          {bankForm.proofPreview && (
                            <button type="button" onClick={(e) => { e.preventDefault(); setViewBankProof(true); }}
                              className="flex items-center gap-2 text-sm font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors bg-indigo-100/50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 px-4 py-2.5 rounded-lg active:scale-95 shadow-sm">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
                              View Document
                            </button>
                          )}
                          {!isBankReadOnly && (
                            <>
                              <button type="button" onClick={() => bankProofRef.current.click()}
                                className="flex items-center gap-2 text-sm font-bold text-gray-700 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white transition-colors bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-600 px-4 py-2.5 rounded-lg active:scale-95 shadow-sm">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path></svg>
                                {bankForm.proofPreview ? "Change File" : "Upload File"}
                              </button>
                              <input type="file" ref={bankProofRef} onChange={handleBankProofUpload} accept="image/*" className="hidden" disabled={isBankReadOnly} />
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {!isBankReadOnly && (
                      <div className="mt-8 flex justify-end border-t border-gray-100 dark:border-slate-700 pt-6">
                        <button
                          type="submit"
                          disabled={!isBankFormValid || submittingBank}
                          className={`px-8 py-3.5 rounded-xl font-bold text-white shadow-lg transition-all duration-300 ${
                            !isBankFormValid
                              ? 'bg-gray-300 dark:bg-slate-600 cursor-not-allowed shadow-none'
                              : submittingBank
                                ? 'bg-indigo-400 cursor-not-allowed'
                                : 'bg-gray-900 dark:bg-slate-200 dark:text-slate-900 hover:bg-gray-800 dark:hover:bg-white active:scale-[0.98]'
                          }`}
                        >
                          {submittingBank ? "Submitting securely..." : "Submit Bank Details"}
                        </button>
                      </div>
                    )}
                  </form>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* ── ANIMATIONS ────────────────────────────────────────────────────── */}
      <style>{`
        @keyframes blowUpModal { 0% { transform: scale(0); } 100% { transform: scale(1); } }
        @keyframes fadeInBg { 0% { background-color: rgba(17, 24, 39, 0); backdrop-filter: blur(0px); } 100% { background-color: rgba(17, 24, 39, 0.6); backdrop-filter: blur(4px); } }
        .bg-fade-in { animation: fadeInBg 0.4s cubic-bezier(0.165, 0.840, 0.440, 1.000) forwards; }
        .modal-blow-up { animation: blowUpModal 0.4s cubic-bezier(0.165, 0.840, 0.440, 1.000) forwards; }
      `}</style>

      {/* ── BANK SUCCESS MODAL ────────────────────────────────────────────── */}
      {isBankSuccessOpen && createPortal(
        <div className="fixed top-0 left-0 w-full h-full z-[9999] flex justify-center items-center bg-fade-in p-4 overflow-auto">
          <div className="w-full max-w-md bg-white dark:bg-slate-800 shadow-2xl rounded-2xl p-8 relative modal-blow-up border border-gray-100 dark:border-slate-700">
            <div className="mt-2 text-center">
              <div className="w-16 h-16 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700/50 rounded-xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
              </div>
              <h3 className="text-xl text-gray-900 dark:text-slate-100 font-bold tracking-tight">Submission Successful</h3>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-2 font-medium leading-relaxed">Your bank details have been securely uploaded. The admin team will verify them shortly.</p>
            </div>
            <button onClick={() => { setIsBankSuccessOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="mt-8 w-full py-3.5 rounded-xl text-white text-sm font-bold bg-gray-900 dark:bg-slate-200 dark:text-slate-900 hover:bg-gray-800 dark:hover:bg-white transition-colors shadow-md active:scale-[0.98]">
              Got it, thanks!
            </button>
          </div>
        </div>,
        document.body
      )}


      {/* ── BANK PROOF IMAGE VIEWER ───────────────────────────────────────── */}
      {viewBankProof && bankForm.proofPreview && createPortal(
        <div className="fixed top-0 left-0 w-full h-full z-[10000] flex items-center justify-center bg-gray-900/90 backdrop-blur-sm p-4 bg-fade-in" onClick={() => setViewBankProof(false)}>
          <button onClick={() => setViewBankProof(false)} className="absolute top-6 right-6 text-white hover:text-red-400 bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
          <img src={bankForm.proofPreview} alt="Enlarged Proof" className="max-w-full max-h-[90vh] object-contain rounded-xl shadow-2xl modal-blow-up" onClick={(e) => e.stopPropagation()} />
        </div>,
        document.body
      )}

      <AlertModal isOpen={globalAlert.isOpen} title={globalAlert.title} message={globalAlert.message} onClose={() => setGlobalAlert({ ...globalAlert, isOpen: false })} />

        {/* ── INVITE MODAL ── */}
      <ShareModal 
        isOpen={isInviteModalOpen} 
        onClose={() => setIsInviteModalOpen(false)} 
        link={inviteLink} // This is your 'CAP-KOL-9824' referral link
      />

    </div>
  );
};

export default Profile;